import {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { useCookies } from "react-cookie";
import { jwtDecode } from "jwt-decode"; // using default import
import ApiManager from "../../api/ApiManager";

const api = new ApiManager();

// Initial state of the authentication
const initialState = {
  user: null, // full userData: { sub, name, email, ... }
  isAuthenticated: false,
  isInitialized: false, // flag to know when the auth state is rehydrated
  isSubbed: null, // flag to know if the user is subscribed
  userCreatedAt: null,
  subscriptionStartDate: null,
  subscriptionEndDate: null,
};

// Reducer to handle auth actions
const authReducer = (state, action) => {
  switch (action.type) {
    case "LOGIN":
      return {
        ...state,
        user: {
          sub: action.payload.sub,
          projectId: action.payload.projectId,
        },
        isAuthenticated: true,
      };
    case "LOGOUT":
      return initialState;
    case "INITIALIZED":
      return {
        ...state,
        isInitialized: true,
      };
    case "SET_SUB":
      return {
        ...state,
        isSubbed: action.payload.isSubbed,
        userCreatedAt: action.payload.userCreatedAt || null,
        subscriptionStartDate: action.payload.subscriptionStartDate || null,
        subscriptionEndDate: action.payload.subscriptionEndDate || null,
      };
    default:
      return state;
  }
};

// Create the Auth context
const AuthContext = createContext();

// AuthProvider component that encapsulates auth logic and context
export const AuthProvider = ({ children }) => {
  const [cookies, setCookie, removeCookie] = useCookies();
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Generic function to retrieve any cookie by name
  const getCookie = useCallback(
    (name) => {
      return cookies[name] || null;
    },
    [cookies]
  );

  // Check subscription status function - esportata per essere usata in Settings.jsx
  const checkSubscription = useCallback(async () => {
    try {
      const response = await api.get("/sub/check", {
        headers: {
          Authorization: `Bearer ${getCookie("id_token")}`,
        },
      });

      console.log("Response from subscription check:", response.data);

      const subStatus = {
        isSubbed: response.data.sub === true,
        userCreatedAt: response.data.CreationDate || null,
        subscriptionStartDate: response.data.SubscriptionStartDate || null,
        subscriptionEndDate: response.data.SubscriptionEndDate || null,
      };

      dispatch({
        type: "SET_SUB",
        payload: subStatus
      });

      return subStatus;
    } catch (error) {
      console.error("Errore nel check sub:", error);
      dispatch({
        type: "SET_SUB",
        payload: {
          isSubbed: false,
          userCreatedAt: null,
          subscriptionStartDate: null,
          subscriptionEndDate: null,
        }
      });
      return { 
        isSubbed: false, 
        userCreatedAt: null,
        subscriptionStartDate: null,
        subscriptionEndDate: null,
      };
    }
  }, [getCookie]);

  // On mount, rehydrate the auth state from the cookie
  useEffect(() => {
    const tokenSub = getCookie("auth_token");
    const projectId = getCookie("project_id");

    if (tokenSub) {
      dispatch({
        type: "LOGIN",
        payload: {
          sub: tokenSub,
          projectId: projectId
        }
      });

      // Check sub status solo 1 volta
      checkSubscription();
    }
    dispatch({ type: "INITIALIZED" });
  }, [cookies.auth_token, cookies.project_id, getCookie, checkSubscription]);

  const login = useCallback(
    async (userData) => {
      try {
        let sub;
        let idToken;
        let projectId = null;

        console.log("Tutti i dati ricevuti nel login:", userData); // log completo

        if (userData.user_sub) {
          // Credentials login
          sub = userData.user_sub;
          idToken = userData.id_token;
          projectId = userData.projects ? userData.projects[0] : userData.project_id;

          console.log("Logged in via credentials.\nsub:", sub, "\nproject_id:", projectId);
        } else {
          // Google login
          const decoded = jwtDecode(userData.data.access_token);
          console.log("Decoded Google Token:", decoded);

          sub = decoded.sub;
          idToken = userData.data.id_token;

          const response = await api.get(`/projects/fetch?user_sub=${sub}`, { headers: { Authorization: `Bearer ${idToken}` } });
          const data = response.data.body ? JSON.parse(response.data.body) : {};
          console.log("Response from projects fetch:", data);
          if (data.projects && data.projects.length > 0) {
            const firstProject = data.projects[0];
            if (firstProject.pk && firstProject.pk.startsWith("project#")) {
              projectId = firstProject.pk.slice(8);
            }
          }
          console.log("Logged in via Google.\nsub:", sub, "\nproject_id:", projectId);
        }

        setCookie("auth_token", sub, { path: "/", maxAge: 3600 });
        setCookie("id_token", idToken, { path: "/", maxAge: 3600 });
        setCookie("project_id", projectId, { path: "/", maxAge: 3600 });

        dispatch({
          type: "LOGIN",
          payload: { sub, projectId },
        });

        return { sub, projectId };
      } catch (error) {
        console.error("Error during login:", error);
        return null;
      }
    },
    [setCookie]
  );

  // Memoized logout function
  const logout = useCallback(() => {
    removeCookie("auth_token", { path: "/" });
    removeCookie("id_token", { path: "/" });
    removeCookie("project_id", { path: "/" });
    dispatch({ type: "LOGOUT" });
  }, [removeCookie]);

  const setIsSubbed = useCallback((value) => {
    dispatch({ type: "SET_SUB", payload: value });
  }, []);

  // Memoized context value to prevent unnecessary re-renders
  const value = useMemo(
    () => ({
      ...state,
      login,
      logout,
      getCookie,
      setIsSubbed,
      checkSubscription, // Esportato per essere utilizzato in Settings.jsx
    }),
    [state, login, logout, getCookie, setIsSubbed, checkSubscription]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use the Auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};